// MARK://dhk8/admin-shell/pages/modules/ReservationsPage
// Reservations admin view. Lives at /reservations.
//
// Lane #163 — date picker amplio (HOY/MAÑANA/7d/RANGO) + campos extra
// (email/sillas/alergias/ocasión/origen) + pill code + render rico.

import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Plus, Trash2, RefreshCw, Phone, Mail, Baby, Accessibility,
  AlertTriangle, Calendar, ChevronRight, LogIn, Armchair, UserX,
} from 'lucide-react'

import * as resv from '../../lib/clients/reservations.js'
import * as floor from '../../lib/clients/floor.js'
import type { FloorTable } from '../../lib/mock-data.js'
import { useAsync, asyncState } from '../../lib/use-async.js'
import { ShareLinkButton } from '../../components/ShareLinkButton.js'

type DateMode = 'today' | 'tomorrow' | 'week' | 'range'

function todayISO(): string {
  const d = new Date()
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}
function shiftISO(days: number): string {
  const d = new Date()
  d.setDate(d.getDate() + days)
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}
function fmtTime(iso: string): string {
  return new Date(iso).toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' })
}
function fmtDate(iso: string): string {
  return new Date(iso).toLocaleDateString('es-MX', { weekday: 'short', day: 'numeric', month: 'short' })
}

function statusBadgeClass(s: resv.ReservationDto['status']): string {
  switch (s) {
    case 'CONFIRMED': return 'adm-badge'
    case 'ARRIVED': return 'adm-badge adm-badge--warn'
    case 'SEATED': return 'adm-badge adm-badge--ok'
    case 'CANCELLED': return 'adm-badge adm-badge--muted'
    case 'NO_SHOW': return 'adm-badge adm-badge--muted'
  }
}
function statusLabel(s: resv.ReservationDto['status']): string {
  switch (s) {
    case 'CONFIRMED': return 'Confirmada'
    case 'ARRIVED': return 'Llegó'
    case 'SEATED': return 'Sentada'
    case 'CANCELLED': return 'Cancelada'
    case 'NO_SHOW': return 'No-show'
  }
}

const OCCASION_LABEL: Record<NonNullable<resv.ReservationDto['occasion']>, string> = {
  birthday: '🎂 Cumpleaños',
  anniversary: '💍 Aniversario',
  date: '❤️ Cita',
  business: '💼 Negocios',
  other: 'Otro',
}

const SOURCE_LABEL: Record<NonNullable<resv.ReservationDto['source']>, string> = {
  walk_in: 'Walk-in',
  phone: 'Llamada',
  web: 'Web',
  instagram: 'Instagram',
  recommended: 'Recomendado',
  other: 'Otro',
}

export default function ReservationsPage() {
  const navigate = useNavigate()
  const [mode, setMode] = useState<DateMode>('today')
  const [date, setDate] = useState<string>(todayISO())
  const [rangeFrom, setRangeFrom] = useState<string>(todayISO())
  const [rangeTo, setRangeTo] = useState<string>(shiftISO(7))

  const queryOpts = useMemo<{ date?: string; from?: string; to?: string; label: string }>(() => {
    if (mode === 'today') return { date: todayISO(), label: fmtDate(`${todayISO()}T12:00:00`) }
    if (mode === 'tomorrow') return { date: shiftISO(1), label: fmtDate(`${shiftISO(1)}T12:00:00`) }
    if (mode === 'week') {
      return {
        from: new Date(`${todayISO()}T00:00:00`).toISOString(),
        to: new Date(`${shiftISO(7)}T23:59:59`).toISOString(),
        label: 'Próximos 7 días',
      }
    }
    return {
      from: new Date(`${rangeFrom}T00:00:00`).toISOString(),
      to: new Date(`${rangeTo}T23:59:59`).toISOString(),
      label: `${fmtDate(`${rangeFrom}T12:00:00`)} → ${fmtDate(`${rangeTo}T12:00:00`)}`,
    }
  }, [mode, rangeFrom, rangeTo])

  const { data, loading, error, reload } = useAsync(
    (s) => {
      const opts: { date?: string; from?: string; to?: string } = {}
      if (queryOpts.date) opts.date = queryOpts.date
      if (queryOpts.from) opts.from = queryOpts.from
      if (queryOpts.to) opts.to = queryOpts.to
      return resv.listReservations(opts, s)
    },
    [queryOpts.date, queryOpts.from, queryOpts.to],
  )

  const [creating, setCreating] = useState(false)
  const [rowBusy, setRowBusy] = useState<string | null>(null)

  // Floor tables para asignar mesa a la reserva (lectura por el grafo, F9.x).
  const layout = useAsync((s) => floor.getLayout(s))
  const tables = useMemo<FloorTable[]>(() => layout.data?.tables ?? [], [layout.data])
  const tableLabel = (id: string | null): string | null => {
    if (!id) return null
    const t = tables.find((x) => x.id === id)
    return t ? `Mesa ${t.code}` : null
  }

  async function setStatus(r: resv.ReservationDto, status: resv.ReservationDto['status']) {
    setRowBusy(r.id)
    try { await resv.updateReservation(r.id, { status }); reload() }
    catch (e) { alert(e instanceof Error ? e.message : 'No se pudo actualizar el estado.') }
    finally { setRowBusy(null) }
  }

  async function assignTable(r: resv.ReservationDto, tableId: string) {
    setRowBusy(r.id)
    try { await resv.updateReservation(r.id, { tableId: tableId || null }); reload() }
    catch (e) { alert(e instanceof Error ? e.message : 'No se pudo asignar la mesa.') }
    finally { setRowBusy(null) }
  }

  const sorted = useMemo(
    () => [...(data ?? [])].sort((a, b) => new Date(a.reservedAt).getTime() - new Date(b.reservedAt).getTime()),
    [data],
  )

  // Group by day para week/range
  const grouped = useMemo(() => {
    if (mode === 'today' || mode === 'tomorrow') {
      return [{ key: queryOpts.label, label: queryOpts.label, rows: sorted }]
    }
    const byDate = new Map<string, resv.ReservationDto[]>()
    for (const r of sorted) {
      const key = r.reservedAt.slice(0, 10)
      if (!byDate.has(key)) byDate.set(key, [])
      byDate.get(key)!.push(r)
    }
    return Array.from(byDate.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([d, rows]) => ({ key: d, label: fmtDate(`${d}T12:00:00`), rows }))
  }, [sorted, mode, queryOpts.label])

  async function handleCancel(r: resv.ReservationDto) {
    if (!confirm(`¿Cancelar reserva de ${r.name}?`)) return
    await resv.cancelReservation(r.id)
    reload()
  }

  return (
    <div className="adm-page">
      <div className="adm-page__head">
        <div>
          <h1 className="adm-page__title">Reservas</h1>
          <p className="adm-page__sub">
            Lista por rango. Visibles también en el POS (drawer izquierdo del Plano).
          </p>
        </div>
        <div className="adm-page__head-actions" style={{ display: 'flex', gap: 8 }}>
          <ShareLinkButton
            label="Copiar enlace de reservas"
            buildUrl={({ slug, liveTenantId }) =>
              slug ? `https://reservar.nintaigo.com/reservar/${slug}`
              : liveTenantId ? `https://reservar.nintaigo.com/reservar/${liveTenantId}`
              : null}
          />
          <button type="button" className="adm-btn" onClick={reload} title="Recargar">
            <RefreshCw size={14} />
          </button>
          <button type="button" className="adm-btn adm-btn--primary" onClick={() => setCreating(true)}>
            <Plus size={14} /> Nueva reserva
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 4, padding: '8px 0' }}>
        {([
          { id: 'today', label: 'Hoy' },
          { id: 'tomorrow', label: 'Mañana' },
          { id: 'week', label: '7 días' },
          { id: 'range', label: 'Rango personalizado' },
        ] as const).map((t) => (
          <button
            key={t.id}
            type="button"
            className={`adm-btn${mode === t.id ? ' adm-btn--primary' : ''}`}
            onClick={() => setMode(t.id)}
          >
            <Calendar size={12} /> {t.label}
          </button>
        ))}
        {mode === 'today' && (
          <input
            type="date"
            value={date}
            onChange={(e) => { setDate(e.target.value); setMode('range'); setRangeFrom(e.target.value); setRangeTo(e.target.value) }}
            className="adm-input"
            style={{ marginLeft: 'auto', maxWidth: 180 }}
            title="Otro día"
          />
        )}
      </div>

      {mode === 'range' && (
        <div className="adm-card" style={{ display: 'flex', gap: 12, padding: 12, alignItems: 'flex-end' }}>
          <label className="adm-field" style={{ flex: 1 }}>
            <span>De</span>
            <input type="date" value={rangeFrom} onChange={(e) => setRangeFrom(e.target.value)} className="adm-input" />
          </label>
          <label className="adm-field" style={{ flex: 1 }}>
            <span>A</span>
            <input type="date" value={rangeTo} onChange={(e) => setRangeTo(e.target.value)} className="adm-input" />
          </label>
        </div>
      )}

      {asyncState(loading, error, reload)}

      {!loading && !error && (
        <>
          {sorted.length === 0 ? (
            <div className="adm-card"><div className="adm-empty">No hay reservas en {queryOpts.label.toLowerCase()}.</div></div>
          ) : (
            grouped.map((g) => (
              <div key={g.key} className="adm-card" style={{ overflowX: 'auto', marginBottom: 12 }}>
                <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--adm-border)', fontWeight: 600 }}>
                  {g.label} <span className="adm-muted" style={{ fontWeight: 400, marginLeft: 8 }}>{g.rows.length} reserva{g.rows.length !== 1 ? 's' : ''}</span>
                </div>
                <table className="adm-table">
                  <thead>
                    <tr>
                      <th>Código</th>
                      <th>Hora</th>
                      <th>Cliente</th>
                      <th>Personas</th>
                      <th>Mesa</th>
                      <th>Contacto</th>
                      <th>Detalles</th>
                      <th>Estado</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {g.rows.map((r) => (
                      <tr key={r.id} onClick={() => navigate(`/reservations/${r.id}`)} style={{ cursor: 'pointer' }} title="Abrir ficha de la reserva">
                        <td>{r.reservationCode ? <span className="adm-badge adm-badge--ok" style={{ fontFamily: 'monospace' }}>{r.reservationCode}</span> : <span className="adm-muted">—</span>}</td>
                        <td><strong>{fmtTime(r.reservedAt)}</strong>{r.durationMin && <div className="adm-muted" style={{ fontSize: 11 }}>{r.durationMin} min</div>}</td>
                        <td>
                          <strong>{r.name}</strong>
                          {r.source && <div className="adm-muted" style={{ fontSize: 11 }}>{SOURCE_LABEL[r.source]}</div>}
                        </td>
                        <td>{r.partySize}</td>
                        <td>
                          {r.status === 'CANCELLED' ? (
                            <span className="adm-muted">{tableLabel(r.tableId) ?? '—'}</span>
                          ) : (
                            <select
                              className="adm-input"
                              style={{ minWidth: 110, fontSize: 12, padding: '2px 4px' }}
                              value={r.tableId ?? ''}
                              disabled={rowBusy === r.id || tables.length === 0}
                              onClick={(e) => e.stopPropagation()}
                              onChange={(e) => { e.stopPropagation(); void assignTable(r, e.target.value) }}
                              title="Asignar mesa"
                              aria-label="Asignar mesa"
                            >
                              <option value="">— Sin mesa —</option>
                              {tables.map((t) => <option key={t.id} value={t.id}>Mesa {t.code} ({t.capacity})</option>)}
                            </select>
                          )}
                        </td>
                        <td onClick={(e) => e.stopPropagation()}>
                          {r.phone && (
                            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                              <Phone size={12} /> <a href={`tel:${r.phone}`} style={{ color: 'inherit' }}>{r.phone}</a>
                            </div>
                          )}
                          {r.email && (
                            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 4, marginTop: 2 }}>
                              <Mail size={12} /> <a href={`mailto:${r.email}`} style={{ color: 'inherit', fontSize: 11 }}>{r.email}</a>
                            </div>
                          )}
                          {!r.phone && !r.email && <span className="adm-muted">—</span>}
                        </td>
                        <td style={{ maxWidth: 280 }}>
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                            {r.occasion && <span className="adm-badge">{OCCASION_LABEL[r.occasion]}</span>}
                            {r.highChair && <span className="adm-badge" style={{ display: 'inline-flex', alignItems: 'center', gap: 2 }}><Baby size={10} /> Silla bebé</span>}
                            {r.wheelchair && <span className="adm-badge" style={{ display: 'inline-flex', alignItems: 'center', gap: 2 }}><Accessibility size={10} /> Accesible</span>}
                          </div>
                          {r.allergies && (
                            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 4, marginTop: 4, color: 'var(--adm-warn, #e0a23c)', fontSize: 11 }}>
                              <AlertTriangle size={11} /> {r.allergies}
                            </div>
                          )}
                          {r.notes && <div className="adm-muted" style={{ fontSize: 11, marginTop: 4 }}>{r.notes}</div>}
                          {!r.occasion && !r.highChair && !r.wheelchair && !r.allergies && !r.notes && <span className="adm-muted">—</span>}
                        </td>
                        <td><span className={statusBadgeClass(r.status)}>{statusLabel(r.status)}</span></td>
                        <td onClick={(e) => e.stopPropagation()}>
                          <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                            {r.status !== 'CANCELLED' && (
                              <>
                                {r.status !== 'ARRIVED' && r.status !== 'SEATED' && (
                                  <button type="button" className="adm-btn adm-btn--sm" disabled={rowBusy === r.id} onClick={() => void setStatus(r, 'ARRIVED')} title="Marcar como llegó">
                                    <LogIn size={12} /> Llegó
                                  </button>
                                )}
                                {r.status !== 'SEATED' && (
                                  <button type="button" className="adm-btn adm-btn--sm" disabled={rowBusy === r.id} onClick={() => void setStatus(r, 'SEATED')} title="Marcar como sentada">
                                    <Armchair size={12} /> Sentada
                                  </button>
                                )}
                                {r.status !== 'SEATED' && r.status !== 'NO_SHOW' && (
                                  <button type="button" className="adm-btn adm-btn--sm" disabled={rowBusy === r.id} onClick={() => void setStatus(r, 'NO_SHOW')} title="Marcar como no-show">
                                    <UserX size={12} /> No-show
                                  </button>
                                )}
                              </>
                            )}
                            {r.status !== 'CANCELLED' && r.status !== 'SEATED' && (
                              <button
                                type="button"
                                className="adm-btn adm-btn--sm adm-btn--danger"
                                disabled={rowBusy === r.id}
                                onClick={() => void handleCancel(r)}
                                title="Cancelar"
                              >
                                <Trash2 size={12} />
                              </button>
                            )}
                            <button type="button" className="adm-btn adm-btn--sm" onClick={() => navigate(`/reservations/${r.id}`)} title="Abrir ficha" aria-label="Abrir ficha">
                              <ChevronRight size={12} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ))
          )}
        </>
      )}

      {creating && (
        <ReservationDialog
          defaultDate={mode === 'tomorrow' ? shiftISO(1) : todayISO()}
          tables={tables}
          onClose={() => setCreating(false)}
          onSaved={() => { setCreating(false); reload() }}
        />
      )}
    </div>
  )
}

function isoDatePart(iso: string): string {
  const d = new Date(iso)
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}
function isoTimePart(iso: string): string {
  const d = new Date(iso)
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`
}

function ReservationDialog({
  defaultDate,
  existing,
  tables,
  onClose,
  onSaved,
}: {
  defaultDate?: string
  existing?: resv.ReservationDto
  tables: FloorTable[]
  onClose: () => void
  onSaved: () => void
}) {
  const isEdit = existing != null
  const [name, setName] = useState(existing?.name ?? '')
  const [phone, setPhone] = useState(existing?.phone ?? '')
  const [email, setEmail] = useState(existing?.email ?? '')
  const [partySize, setPartySize] = useState(existing?.partySize ?? 2)
  const [date, setDate] = useState(existing ? isoDatePart(existing.reservedAt) : (defaultDate ?? todayISO()))
  const [time, setTime] = useState(existing ? isoTimePart(existing.reservedAt) : '19:00')
  const [durationMin, setDurationMin] = useState(existing?.durationMin ?? 90)
  const [tableId, setTableId] = useState(existing?.tableId ?? '')
  const [highChair, setHighChair] = useState(existing?.highChair ?? false)
  const [wheelchair, setWheelchair] = useState(existing?.wheelchair ?? false)
  const [occasion, setOccasion] = useState<'' | NonNullable<resv.ReservationDto['occasion']>>(existing?.occasion ?? '')
  const [source, setSource] = useState<'' | NonNullable<resv.ReservationDto['source']>>(existing?.source ?? 'phone')
  const [allergies, setAllergies] = useState(existing?.allergies ?? '')
  const [notes, setNotes] = useState(existing?.notes ?? '')
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!name.trim()) { setErr('Nombre requerido'); return }
    setBusy(true); setErr(null)
    try {
      const reservedAt = new Date(`${date}T${time}:00`).toISOString()
      if (isEdit && existing) {
        await resv.updateReservation(existing.id, {
          name: name.trim(),
          partySize,
          reservedAt,
          durationMin,
          highChair,
          wheelchair,
          tableId: tableId || null,
          phone: phone.trim() || null,
          email: email.trim() || null,
          notes: notes.trim() || null,
          allergies: allergies.trim() || null,
          occasion: occasion || null,
          source: source || null,
        })
      } else {
        const input: resv.CreateReservationInput = {
          name: name.trim(),
          partySize,
          reservedAt,
          durationMin,
          highChair,
          wheelchair,
        }
        if (phone.trim()) input.phone = phone.trim()
        if (email.trim()) input.email = email.trim()
        if (notes.trim()) input.notes = notes.trim()
        if (allergies.trim()) input.allergies = allergies.trim()
        if (occasion) input.occasion = occasion
        if (source) input.source = source
        await resv.createReservation(input)
      }
      onSaved()
    } catch (e2) {
      setErr(e2 instanceof Error ? e2.message : 'Error')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="adm-modal-bg" role="dialog" aria-modal>
      <form className="adm-modal" onSubmit={(e) => void handleSubmit(e)} style={{ maxWidth: 560 }}>
        <h3 style={{ margin: 0 }}>{isEdit ? 'Editar reserva' : 'Nueva reserva'}</h3>

        <label className="adm-field">
          <span>Nombre</span>
          <input value={name} onChange={(e) => setName(e.target.value)} autoFocus className="adm-input" required />
        </label>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
          <label className="adm-field">
            <span>Teléfono</span>
            <input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+52..." className="adm-input" />
          </label>
          <label className="adm-field">
            <span>Email</span>
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="cliente@..." className="adm-input" />
          </label>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8 }}>
          <label className="adm-field">
            <span>Fecha</span>
            <input type="date" value={date} onChange={(e) => setDate(e.target.value)} min={isEdit ? undefined : todayISO()} className="adm-input" required />
          </label>
          <label className="adm-field">
            <span>Hora</span>
            <input type="time" value={time} onChange={(e) => setTime(e.target.value)} className="adm-input" required />
          </label>
          <label className="adm-field">
            <span>Duración (min)</span>
            <input type="number" min={15} max={600} step={15} value={durationMin} onChange={(e) => setDurationMin(Math.max(15, Number(e.target.value) || 90))} className="adm-input" />
          </label>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8 }}>
          <label className="adm-field">
            <span>Personas</span>
            <input type="number" min={1} max={50} value={partySize} onChange={(e) => setPartySize(Math.max(1, Number(e.target.value) || 1))} className="adm-input" />
          </label>
          <label className="adm-field">
            <span>Ocasión</span>
            <select value={occasion} onChange={(e) => setOccasion(e.target.value as typeof occasion)} className="adm-input">
              <option value="">—</option>
              <option value="birthday">Cumpleaños</option>
              <option value="anniversary">Aniversario</option>
              <option value="date">Cita</option>
              <option value="business">Negocios</option>
              <option value="other">Otro</option>
            </select>
          </label>
          <label className="adm-field">
            <span>Origen</span>
            <select value={source} onChange={(e) => setSource(e.target.value as typeof source)} className="adm-input">
              <option value="walk_in">Walk-in</option>
              <option value="phone">Llamada</option>
              <option value="web">Web</option>
              <option value="instagram">Instagram</option>
              <option value="recommended">Recomendado</option>
              <option value="other">Otro</option>
            </select>
          </label>
        </div>

        <div style={{ display: 'flex', gap: 16, padding: '4px 0' }}>
          <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
            <input type="checkbox" checked={highChair} onChange={(e) => setHighChair(e.target.checked)} />
            <Baby size={14} /> Silla para bebé
          </label>
          <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
            <input type="checkbox" checked={wheelchair} onChange={(e) => setWheelchair(e.target.checked)} />
            <Accessibility size={14} /> Accesibilidad
          </label>
        </div>

        <label className="adm-field">
          <span>Mesa (opcional)</span>
          <select value={tableId} onChange={(e) => setTableId(e.target.value)} className="adm-input" disabled={tables.length === 0}>
            <option value="">— Sin asignar —</option>
            {tables.map((t) => <option key={t.id} value={t.id}>Mesa {t.code} ({t.capacity} pers.)</option>)}
          </select>
        </label>

        <label className="adm-field">
          <span>Alergias / restricciones</span>
          <input value={allergies} onChange={(e) => setAllergies(e.target.value)} placeholder="Maní, gluten, vegetariano..." className="adm-input" />
        </label>

        <label className="adm-field">
          <span>Notas (opcional)</span>
          <input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Mesa cerca ventana, sorpresa..." className="adm-input" />
        </label>

        {err && <div className="adm-banner adm-banner--danger" role="alert">{err}</div>}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button type="button" className="adm-btn" onClick={onClose} disabled={busy}>Cancelar</button>
          <button type="submit" className="adm-btn adm-btn--primary" disabled={busy}>
            {busy ? 'Guardando…' : isEdit ? 'Guardar' : 'Crear'}
          </button>
        </div>
      </form>
    </div>
  )
}
